/**
 * Signalling protocol, version 1. JSON text frames over one WebSocket per
 * participant. The server only ever relays between a room's host and one of
 * its peers; media never crosses it.
 */

export const PROTOCOL_VERSION = 2;

export const LIMITS = {
  /** Upper bound for one encoded frame; the server's WebSocket `maxPayload`. */
  maxMessageBytes: 256 * 1024,
  /**
   * Cap for frames from anyone but the host. Peers only ever send answers and
   * candidates (a few kB); keeping them small bounds what a peer can queue for the host.
   */
  maxPeerMessageBytes: 32 * 1024,
  /** Secrets are 128 random bits or more; base64url of 16 bytes is 22 characters. */
  minKeyLength: 22,
  maxSdpLength: 200_000,
  maxCandidateLength: 2048,
  maxNameLength: 64,
  maxIdLength: 64,
  maxReasonLength: 200,
  maxErrorMessageLength: 300,
  maxUrlLength: 512,
  maxCredentialLength: 512,
  maxIceServers: 8,
  maxUrlsPerIceServer: 8,
  maxPeersListed: 1024,
  /** Banned participants a host may hand the server in one message. */
  maxBannedListed: 1024,
  maxViewers: 1000,
} as const;

export type PeerRole = 'viewer' | 'guest';

export type ErrorCode =
  | 'invalid-message'
  | 'unsupported-version'
  | 'room-not-found'
  | 'room-full'
  | 'server-full'
  | 'guests-not-allowed'
  | 'invite-invalid'
  | 'invite-expired'
  | 'banned'
  | 'bad-host-key'
  | 'rate-limited'
  | 'not-joined'
  | 'already-joined'
  | 'peer-not-found'
  | 'host-absent'
  | 'forbidden'
  | 'replaced';

export const ERROR_CODES: readonly ErrorCode[] = [
  'invalid-message',
  'unsupported-version',
  'room-not-found',
  'room-full',
  'server-full',
  'guests-not-allowed',
  'invite-invalid',
  'invite-expired',
  'banned',
  'bad-host-key',
  'rate-limited',
  'not-joined',
  'already-joined',
  'peer-not-found',
  'host-absent',
  'forbidden',
  'replaced',
];

/** Structurally identical to the DOM's RTCIceServer, declared here to stay DOM-free. */
export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface PeerDescriptor {
  peerId: string;
  role: PeerRole;
  name: string;
  /** Guests only: the participant their invite names. Stable across reconnects and tabs. */
  participantId?: string;
}

export interface IceCandidate {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment: string | null;
}

/**
 * Payload relayed verbatim between the host and one peer. `session` names one
 * RTCPeerConnection generation chosen by the host, so that both ends can drop
 * signals that belong to a connection which has since been rebuilt.
 */
export type Signal =
  /**
   * `seq` numbers the offers of one session. The answer echoes it, so the host can
   * tell an answer to its current offer from a late answer to one it has withdrawn.
   */
  | { kind: 'offer'; session: string; sdp: string; seq?: number }
  | { kind: 'answer'; session: string; sdp: string; seq?: number }
  /** `candidate: null` marks end-of-candidates. */
  | { kind: 'candidate'; session: string; candidate: IceCandidate | null }
  /** Peer asks the host to repair or rebuild the connection it currently knows as `session`. */
  | { kind: 'restart'; session: string | null }
  /** Guest tells the host which of its tracks currently carry media. */
  | { kind: 'media-state'; video: boolean; audio: boolean }
  /** Host tells a guest it waits for approval; no offer comes until `admitted`. */
  | { kind: 'lobby' }
  | { kind: 'admitted' };

export type SignalKind = Signal['kind'];

/** Signal kinds each side may originate. The host is always the offerer. */
export const HOST_SIGNAL_KINDS: readonly SignalKind[] = ['offer', 'candidate', 'lobby', 'admitted'];
export const PEER_SIGNAL_KINDS: readonly SignalKind[] = ['answer', 'candidate', 'restart', 'media-state'];

// ---------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------

/**
 * Create a room (no `roomId`/`hostKey`) or reclaim one (both present). The room
 * id is derived from the host key, so a reclaim also works after the server
 * lost its state.
 */
export interface OpenRoomMessage {
  type: 'open-room';
  v: number;
  roomId?: string;
  hostKey?: string;
  maxViewers: number;
  allowGuests: boolean;
  /**
   * The host only uses relayed candidates. Viewers then need TURN too, so they are given
   * credentials; otherwise only the host and guests are.
   */
  relay?: boolean;
  /** Id of the shared guest link currently valid; absent while there is none. */
  sharedLinkId?: string;
  /** Participants banned by this host, restored after a server restart lost them. */
  banned?: string[];
}

/** Changes room policy while the room is open. Fields left out are unchanged. */
export interface UpdateRoomMessage {
  type: 'update-room';
  allowGuests?: boolean;
  /** A new shared-link id (rotation), or null to switch the shared link off. */
  sharedLinkId?: string | null;
  /** Participants to ban from now on. */
  ban?: string[];
}

/**
 * Asks the server to sign a guest invitation. `subject` is a participant id for a personal
 * invite, or the shared-link id for the shared link. Only the server holds the signing
 * routine; the host only decides what to sign.
 */
export interface CreateInviteMessage {
  type: 'create-invite';
  requestId: string;
  subject: string;
  shared: boolean;
  /** Date.now()-based expiry. */
  expiresAt: number;
}

/**
 * `peerKey` echoes the key a previous `joined` issued for this room and resumes that
 * peer id; a key the server did not issue is ignored and a new identity is assigned.
 * `guestToken` comes from the guest link and is required for `role: 'guest'`.
 */
export interface JoinMessage {
  type: 'join';
  v: number;
  roomId: string;
  role: PeerRole;
  name: string;
  peerKey?: string;
  /** Required for `role: 'guest'`: a personal or shared invite token from the guest link. */
  inviteToken?: string;
}

/** `to` is required from the host and forbidden from peers. */
export interface SignalOutMessage {
  type: 'signal';
  to?: string;
  signal: Signal;
}

export interface KickMessage {
  type: 'kick';
  peerId: string;
  reason?: string;
  /** Also ban the peer's participant for the rest of the room's life. Default false. */
  ban?: boolean;
}

export interface CloseRoomMessage {
  type: 'close-room';
}

export interface LeaveMessage {
  type: 'leave';
}

export interface PingMessage {
  type: 'ping';
  t: number;
}

export type ClientMessage =
  | OpenRoomMessage
  | UpdateRoomMessage
  | CreateInviteMessage
  | JoinMessage
  | SignalOutMessage
  | KickMessage
  | CloseRoomMessage
  | LeaveMessage
  | PingMessage;

// ---------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------

export interface RoomOpenedMessage {
  type: 'room-opened';
  roomId: string;
  hostKey: string;
  reclaimed: boolean;
  /** Peers currently connected to the room. */
  peers: PeerDescriptor[];
  iceServers?: IceServer[];
}

export interface JoinedMessage {
  type: 'joined';
  peerId: string;
  peerKey: string;
  hostPresent: boolean;
  /** Guests: the participant this connection is. */
  participantId?: string;
  /**
   * Guests who came through the shared link: a personal token for the participant they were
   * given, so a reload or reconnect comes back as the same participant.
   */
  inviteToken?: string;
  /** Minted for this peer; TURN credentials in it are short-lived (TURN REST usernames carry the expiry). */
  iceServers?: IceServer[];
}

export interface RoomUpdatedMessage {
  type: 'room-updated';
  allowGuests: boolean;
  sharedLinkId: string | null;
}

export interface InviteMessage {
  type: 'invite';
  requestId: string;
  token: string;
  expiresAt: number;
}

export interface PeerJoinedMessage {
  type: 'peer-joined';
  peer: PeerDescriptor;
}

export type PeerLeftReason = 'left' | 'disconnected' | 'kicked';

export interface PeerLeftMessage {
  type: 'peer-left';
  peerId: string;
  reason: PeerLeftReason;
}

/** `from` is set when delivered to the host and absent when delivered to a peer. */
export interface SignalInMessage {
  type: 'signal';
  from?: string;
  signal: Signal;
}

export interface HostLeftMessage {
  type: 'host-left';
}

export interface HostJoinedMessage {
  type: 'host-joined';
}

export interface KickedMessage {
  type: 'kicked';
  reason: string;
}

export type RoomClosedReason = 'closed' | 'expired';

export interface RoomClosedMessage {
  type: 'room-closed';
  reason: RoomClosedReason;
}

export interface ErrorMessage {
  type: 'error';
  code: ErrorCode;
  message: string;
  /** The peer a failed host request referred to. */
  peerId?: string;
}

export interface PongMessage {
  type: 'pong';
  t: number;
}

export type ServerMessage =
  | RoomOpenedMessage
  | RoomUpdatedMessage
  | InviteMessage
  | JoinedMessage
  | PeerJoinedMessage
  | PeerLeftMessage
  | SignalInMessage
  | HostLeftMessage
  | HostJoinedMessage
  | KickedMessage
  | RoomClosedMessage
  | ErrorMessage
  | PongMessage;

/** WebSocket close codes used by the server besides the standard ones. */
export const CLOSE_CODES = {
  replaced: 4000,
  joinTimeout: 4001,
  misbehaving: 4002,
  kicked: 4003,
  roomClosed: 4004,
} as const;
